import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import 'react-tagsinput/react-tagsinput.css';
import {
  Alert, Button, Card, CardHeader, CardBody, Col, Container, Form, FormGroup, FormText,
  Input, InputGroup, InputGroupAddon, Label, Row, ModalHeader, ModalBody, ModalFooter, Modal,
} from 'reactstrap';
import { register, setError } from '../../../actions/user';
import { getErrorMessageKey } from '../../../utils/helper';
import { getCompanyColors, getCompanyRegParams } from '../../../utils/Api';
import css from '../../../containers/_Full/css';

const applyCSS = () => {
  function replaceAll(string, search, replace) {
    if (replace === undefined) {
      return string.toString();
    }
    return string.split(search).join(replace);
  }

  let companyColors = { primary_color: '55, 177, 174', secondary_color: '221, 236, 235' };
  let companyBg = '';
  const companyColorsPromise = getCompanyColors();
  companyColorsPromise.then((newCompanyColors) => {
    localStorage.setItem('colors', JSON.stringify(newCompanyColors.rgb));
    localStorage.setItem('bg', JSON.stringify(newCompanyColors.bg));
  }).then(() => {
    const localCompanyColors = localStorage.getItem('colors');
    const localCompanyBg = localStorage.getItem('bg');
    companyColors = (localCompanyColors === 'undefined' || !localCompanyColors)
      ? companyColors
      : JSON.parse(localCompanyColors);
    companyBg = (localCompanyBg === 'undefined' || !localCompanyBg)
      ? companyBg
      : JSON.parse(localCompanyBg);
  }).then(() => {
    let style = document.getElementById('company_colors');
    if (!style) {
      style = document.createElement('style');
      style.setAttribute('id', 'company_colors');
    }
    let rCSS = replaceAll(css, 'primary_color', companyColors.primary_color);
    rCSS = replaceAll(rCSS, 'secondary_color', companyColors.secondary_color);
    rCSS = replaceAll(rCSS, 'company_bg', companyBg);
    style.innerHTML = rCSS;
    document.head.appendChild(style);
  });
};

const mapStateToProps = ({ user, i18n: { translations } }) => ({
  user: user || {},
  translations,
  errorMessage: user.message,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  sendData: register,
  clearError: () => setError(''),
}, dispatch);

const Register = ({ intl, user, errorMessage, translations, sendData, clearError }) => {
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [state, setState] = useState({
    salutation: '', // optional
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const set = fields => setState(rest => ({ ...rest, ...fields }));

  const getRegistrationParams = () => {
    getCompanyRegParams().then(({ error, ...regParams }) => {
      if (error) {
        const errorMessage = getErrorMessageKey(translations, error.code) || error.msg;
        setError(errorMessage || 'Error getting registration params');
      } else {
        set(regParams);
        setError(regParams.allow_registration ? '' : 'register_allowed_error');
      }
    });
    setRegistered(!user.id);
  };

  useEffect(() => {
    getRegistrationParams();
    applyCSS();
  }, []);

  const handleChange = ({ target: { id, value, type, checked } }) => {
    set({ [id]: type === 'checkbox' ? checked : value });
    if (error) setError('');
    if (errorMessage) clearError('');
  };

  const submit = () => {
    const {
      first_name, last_name, email, password, confirmPassword,
      confirm_gdpr, confirm_terms, confirm_withdrawStorage,
    } = state;

    if (!first_name) setError('error_fill_firstName');
    else if (!last_name) setError('error_fill_lastName');
    else if (!email) setError('error_fill_email');
    else if (!password) setError('error_fill_password');
    else if (!confirmPassword) setError('error_fill_confirmPassword');
    else if (password !== confirmPassword) setError('profile__doesNotMatch');
    else if (!confirm_gdpr) setError('error_fill_gdpr');
    else if (!confirm_terms) setError('error_fill_terms');
    else if (!confirm_withdrawStorage) setError('error_fill_withdrawStorage');
    else {
      const fields = { first_name, last_name, email, password };

      if (salutation) { fields.salutation = salutation; }

      sendData(fields);
    }
  };

  const {
    salutation, first_name, last_name, email, password, confirmPassword, confirm_gdpr, confirm_terms,
    confirm_withdrawStorage, link_gdpr, link_terms, allow_registration,
  } = state;

  if (redirect) {
    return <Redirect to="/login" />;
  }

  return (
    <div className="app flex-row align-items-center">
      <Modal isOpen={registered && user.id}>
        <ModalBody>{`Please check your inbox ${
          user.email_from ? `for a mail from ${user.email_from}` : ''
        } and confirm your email address and our Terms of use and data protection.`}</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setRedirect(true)}>
            <FormattedMessage id="login_title" />
          </Button>
        </ModalFooter>
      </Modal>
      <Container>
        <Row className="justify-content-center">
          <Col xs="12" md="12">
            <Card className="mb-0">
              <CardHeader><FormattedMessage id="header_register_user" /></CardHeader>
              <CardBody>
                <Form action="" method="post" encType="multipart/form-data" className="form-horizontal">
                  <Col lg="6" md="6" sm="12" xs="12" className="fl pb-3">
                    <FormGroup row>
                      <Col md="3">
                        <Label htmlFor="salutation"><FormattedMessage id="profile_salutation" /></Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Input
                          type="text"
                          id="salutation"
                          autoComplete="name"
                          placeholder={intl.formatMessage({ id: 'profile_salutation' })}
                          value={salutation}
                          onChange={handleChange}
                          disabled={!allow_registration}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3">
                        <Label htmlFor="first_name"><FormattedMessage id="profile_firstName" /> *</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Input
                          type="text"
                          id="first_name"
                          autoComplete="name"
                          placeholder={intl.formatMessage({ id: 'profile_firstName' })}
                          value={first_name}
                          onChange={handleChange}
                          disabled={!allow_registration}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3">
                        <Label htmlFor="last_name"><FormattedMessage id="profile_lastName" /> *</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Input
                          type="text"
                          id="last_name"
                          autoComplete="name"
                          placeholder={intl.formatMessage({ id: 'profile_lastName' })}
                          value={last_name}
                          onChange={handleChange}
                          disabled={!allow_registration}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3">
                        <Label htmlFor="email"><FormattedMessage id="profile_email" /> *</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Input
                          type="email"
                          id="email"
                          autoComplete="email"
                          placeholder={intl.formatMessage({ id: 'profile_email' })}
                          value={email}
                          onChange={handleChange}
                          disabled={!allow_registration}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col lg="6" md="6" sm="12" xs="12" className="fl pb-3">
                    <FormGroup row>
                      <Col md="6">
                        <Label htmlFor="password"><FormattedMessage id="profile__password" /> *</Label>
                      </Col>
                      <Col md="6">
                        <Input
                          type="password"
                          id="password"
                          autoComplete="password"
                          placeholder={intl.formatMessage({ id: 'profile__password' })}
                          value={password}
                          onChange={handleChange}
                          disabled={!allow_registration}
                        />
                        <FormText className="help-block"><FormattedMessage id="profile__enterComplex" /></FormText>
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="6">
                        <Label htmlFor="confirmPassword">
                          <FormattedMessage id="profile__confirmPassword" /> *
                        </Label>
                      </Col>
                      <Col md="6">
                        <Input
                          type="password"
                          id="confirmPassword"
                          autoComplete="confirmPassword"
                          placeholder={intl.formatMessage({ id: 'profile__confirmPassword' })}
                          value={confirmPassword}
                          onChange={handleChange}
                          disabled={!allow_registration}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="6">
                        <Label htmlFor="confirm_gdpr">
                          <a href={link_gdpr} target={user.options.all_in_one_tab ? "_self" : "_blank"} rel="noopener noreferrer">
                            {intl.formatMessage({ id: 'register_confirm_gdpr' })}
                          </a> *
                        </Label>
                      </Col>
                      <Col md="6">
                        <Input
                          type="checkbox"
                          id="confirm_gdpr"
                          autoComplete="confirm_gdpr"
                          checked={confirm_gdpr}
                          onChange={handleChange}
                          disabled={!allow_registration}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="6">
                        <Label htmlFor="confirm_terms">
                          <a href={link_terms} target={user.options.all_in_one_tab ? "_self" : "_blank"} rel="noopener noreferrer">
                            {intl.formatMessage({ id: 'register_confirm_terms' })}
                          </a> *
                        </Label>
                      </Col>
                      <Col md="6">
                        <Input
                          type="checkbox"
                          id="confirm_terms"
                          autoComplete="confirm_terms"
                          checked={confirm_terms}
                          onChange={handleChange}
                          disabled={!allow_registration}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="6"><Label htmlFor="confirm_withdrawStorage">
                        <FormattedMessage id="register_confirm_withdrawStorage" /> *</Label>
                      </Col>
                      <Col md="6">
                        <Input
                          type="checkbox"
                          id="confirm_withdrawStorage"
                          autoComplete="confirm_withdrawStorage"
                          checked={confirm_withdrawStorage}
                          onChange={handleChange}
                          disabled={!allow_registration}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col xs="12" className="clear">
                    {(error || errorMessage) && (
                      <Alert color="danger"><FormattedMessage id={error || errorMessage} /></Alert>
                    )}
                  </Col>
                </Form>
                <Row>
                  <Col xs="12" md="12">
                    <InputGroup className="pb-3">
                      <InputGroupAddon className="ml-auto" addonType="append">
                        <Button
                          onClick={submit}
                          color="primary"
                          size="lg"
                          disabled={!allow_registration}
                        ><FormattedMessage id="overall_input_accept" /></Button>
                      </InputGroupAddon>
                    </InputGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

Register.propTypes = {
  intl: PropTypes.objectOf(PropTypes.any),
  user: PropTypes.objectOf(PropTypes.any),
  errorMessage: PropTypes.string.isRequired,
  translations: PropTypes.objectOf(PropTypes.object).isRequired,
  sendData: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(Register));



// WEBPACK FOOTER //
// ./src/views/Pages/_Register/index.js